<template>
  <div class="app-language-selector" ref="languageSelectorRef">
    <button @click="toggleLanguageMenu" class="app-language-selector__btn" :title="$t('common.language')">
      <div class="app-language-selector__current">
        <img :src="currentLanguageFlag" :alt="currentLanguage" class="app-language-selector__flag" />
        <span class="app-language-selector__code hide-mobile">{{ currentLanguage.toUpperCase() }}</span>
      </div>
      <svg class="app-language-selector__dropdown-icon" width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M1 1L5 5L9 1" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    </button>
    
    <transition name="app-menu-fade">
      <div v-if="menuVisible" class="app-language-selector__menu-wrapper">
        <ul class="app-language-selector__menu">
          <li @click="changeLanguage('en')" :class="{ 'app-language-selector__item--active': currentLanguage === 'en' }" class="app-language-selector__item">
            <img src="/assets/flags/england.svg" alt="English" class="app-language-selector__flag" />
            <span>{{ $t('common.english') }}</span>
          </li>
          <li @click="changeLanguage('fr')" :class="{ 'app-language-selector__item--active': currentLanguage === 'fr' }" class="app-language-selector__item">
            <img src="/assets/flags/france.svg" alt="French" class="app-language-selector__flag" />
            <span>{{ $t('common.french') }}</span>
          </li>
          <li @click="changeLanguage('ar')" :class="{ 'app-language-selector__item--active': currentLanguage === 'ar' }" class="app-language-selector__item">
            <img src="/assets/flags/tunisia.svg" alt="Arabic" class="app-language-selector__flag" />
            <span>{{ $t('common.arabic') }}</span>
          </li>
        </ul>
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
/// <reference types="vue" />
// @ts-ignore
import { computed, ref, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import { setLocale } from '@/i18n'

// Reactive state
const menuVisible = ref<boolean>(false)
const languageSelectorRef = ref<HTMLElement | null>(null)

// i18n
const { locale } = useI18n()

// Computed properties
const currentLanguage = computed((): string => {
  return locale.value
})

const currentLanguageFlag = computed((): string => {
  switch(currentLanguage.value) {
    case 'en': return '/assets/flags/england.svg'
    case 'fr': return '/assets/flags/france.svg'
    case 'ar': return '/assets/flags/tunisia.svg'
    default: return '/assets/flags/england.svg'
  }
})

// Methods
const toggleLanguageMenu = (): void => {
  menuVisible.value = !menuVisible.value
  if (menuVisible.value) {
    // Add event listeners to detect clicks outside and Escape presses
    document.addEventListener('click', handleClickOutside)
    document.addEventListener('keydown', handleKeydown)
  } else {
    removeMenuEventListeners()
  }
}

const closeLanguageMenu = (): void => {
  menuVisible.value = false
  removeMenuEventListeners()
}

const removeMenuEventListeners = (): void => {
  document.removeEventListener('click', handleClickOutside)
  document.removeEventListener('keydown', handleKeydown)
}

const changeLanguage = (lang: string): void => {
  try {
    // Make sure the language code is valid
    if (!['en', 'fr', 'ar'].includes(lang)) {
      return
    }

    // Call the setLocale function
    setLocale(lang)

    // Double check that the language was actually changed
    if (window.i18n && window.i18n.global && window.i18n.global.locale.value !== lang) {
      // Direct set as fallback
      window.i18n.global.locale.value = lang
    }

    closeLanguageMenu()
  } catch (e) {
    // Silent error handling
  }
}

const handleClickOutside = (event: MouseEvent): void => {
  // Check if clicked outside of the language selector
  if (languageSelectorRef.value && !languageSelectorRef.value.contains(event.target as Node)) {
    closeLanguageMenu()
  }
}

const handleKeydown = (event: KeyboardEvent): void => {
  if (event.key === 'Escape') {
    closeLanguageMenu()
  }
}

// Lifecycle
onBeforeUnmount(() => {
  // Clean up event listeners
  removeMenuEventListeners()
})
</script>

<style scoped>
.app-language-selector {
  position: relative;
  margin-right: var(--space-md);
}

.app-language-selector__btn {
  display: flex;
  align-items: center;
  gap: var(--space-xs);
  background: transparent;
  border: none;
  border-radius: var(--radius-lg);
  color: white;
  cursor: pointer;
  padding: var(--space-xs) var(--space-sm);
  transition: all var(--transition-normal) var(--easing);
  position: relative;
}

.app-language-selector__btn:hover {
  background: transparent;
  transform: translateY(-1px);
}

.app-language-selector__current {
  display: flex;
  align-items: center;
  gap: var(--space-xs);
}

.app-language-selector__flag {
  width: 20px;
  height: 15px;  object-fit: cover;
  border-radius: var(--radius-xs);
}

.app-language-selector__code {
  font-weight: 600;
  text-transform: uppercase;
  font-size: var(--font-size-sm);
  letter-spacing: 0.5px;
}

.app-language-selector__dropdown-icon {
  transition: transform var(--transition-normal) var(--easing);
  opacity: 0.8;
}

.app-language-selector__btn:hover .app-language-selector__dropdown-icon {
  transform: translateY(1px);
  opacity: 1;
}

.app-language-selector__menu-wrapper {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  z-index: 1001;
  width: 200px;
}

.app-language-selector__menu {
  background: white;
  border: 1px solid var(--neutral-200);
  border-radius: var(--radius-lg);
  list-style: none;
  padding: var(--space-sm);
  margin: 0;
  overflow: hidden;
}

.app-language-selector__item {
  padding: var(--space-sm) var(--space-md);
  cursor: pointer;
  transition: all var(--transition-normal) var(--easing);
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  color: var(--neutral-700);
  border-radius: var(--radius-md);
  font-weight: 500;
  position: relative;
  overflow: hidden;
}

.app-language-selector__item::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  width: 3px;
  height: 100%;
  background: linear-gradient(135deg, #3b82f6, #1d4ed8);
  transform: scaleY(0);
  transition: transform var(--transition-normal) var(--easing);
}

.app-language-selector__item:hover {
  background: rgba(59, 130, 246, 0.08);
  color: var(--neutral-800);
  transform: translateX(4px);
}

.app-language-selector__item:hover::before {
  transform: scaleY(1);
}

.app-language-selector__item--active {
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(29, 78, 216, 0.05));
  color: var(--primary-600);
  font-weight: 600;
}

.app-language-selector__item--active::before {
  transform: scaleY(1);
}

.app-language-selector__item .app-language-selector__flag {
  width: 24px;
  height: 18px;
  transition: transform var(--transition-normal) var(--easing);
}

.app-language-selector__item:hover .app-language-selector__flag {
  transform: scale(1.1);
}

.app-menu-fade-enter-active,
.app-menu-fade-leave-active {
  transition: all 0.3s var(--easing);
  transform-origin: top right;
}

.app-menu-fade-enter-from,
.app-menu-fade-leave-to {
  opacity: 0;
  transform: scale(0.95) translateY(-10px);
}

/* Set max height to prevent menu from extending beyond viewport */
.app-language-selector__menu {
  max-height: calc(100vh - 120px);
  overflow-y: auto;
}

/* Mobile responsiveness */
@media (max-width: 768px) {
  .hide-mobile {
    display: none;
  }

  .app-language-selector {
    margin-right: 0;
  }
  
  .app-language-selector__btn {
    padding: var(--space-xs);
  }
  
  .app-language-selector__menu-wrapper {
    position: fixed;
    top: 56px;
    bottom: auto;
    left: var(--space-md);
    right: var(--space-md);
    z-index: 1001;
    width: auto;
  }
  
  .app-language-selector__menu {
    width: 100%;
    max-width: 100%;
    border-radius: var(--radius-lg);
  }
  
  .app-language-selector__item {
    padding: var(--space-md);
    font-size: var(--font-size-md);
  }
  
  .app-language-selector__item .app-language-selector__flag {
    width: 28px;
    height: 21px;
  }
}

/* Dark mode support */
@media (prefers-color-scheme: dark) {
  .app-language-selector__menu {
    background: rgba(30, 41, 59, 0.95);
    border-color: rgba(255, 255, 255, 0.1);
  }
  
  .app-language-selector__item {
    color: var(--neutral-200);
  }
  
  .app-language-selector__item:hover {
    background: rgba(59, 130, 246, 0.15);
    color: var(--neutral-100);
  }
  
  .app-language-selector__item--active {
    background: linear-gradient(135deg, rgba(59, 130, 246, 0.2), rgba(29, 78, 216, 0.1));
    color: #60a5fa;
  }
}
</style>
